import { api } from "./client";

export interface OrganizationRead {
  id: string;
  clinic_id: string;
  name: string;
  legal_name: string | null;
  inn: string | null;
  address: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  discount_percent: number;
  notes: string | null;
  is_active: boolean;
}

export interface OrganizationCreatePayload {
  name: string;
  legal_name?: string | null;
  inn?: string | null;
  address?: string | null;
  contact_name?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  discount_percent: number;
  notes?: string | null;
}

export async function listOrganizations(params: {
  q?: string;
  is_active?: boolean;
  limit?: number;
}): Promise<{ items: OrganizationRead[]; total: number }> {
  const { data } = await api.get("/references/organizations", { params });
  return data;
}

export async function createOrganization(
  payload: OrganizationCreatePayload,
): Promise<OrganizationRead> {
  const { data } = await api.post<OrganizationRead>("/references/organizations", payload);
  return data;
}

export async function updateOrganization(
  id: string,
  payload: Partial<OrganizationCreatePayload> & { is_active?: boolean },
): Promise<OrganizationRead> {
  const { data } = await api.patch<OrganizationRead>(
    `/references/organizations/${id}`,
    payload,
  );
  return data;
}

export async function deleteOrganization(id: string): Promise<void> {
  await api.delete(`/references/organizations/${id}`);
}

export interface ExaminationTypeRead {
  id: string;
  clinic_id: string;
  code: string;
  name: string;
  default_price: number;
  currency: string;
  default_validity_days: number;
  required_specialties: string[];
  required_lab_tests: string[];
  required_functional: string[];
  sex_restriction: string | null;
  age_restriction_min: number | null;
  age_restriction_max: number | null;
  notes: string | null;
  is_active: boolean;
}

export interface ExaminationTypeCreatePayload {
  code: string;
  name: string;
  default_price: number;
  currency: string;
  default_validity_days: number;
  required_specialties: string[];
  required_lab_tests: string[];
  required_functional: string[];
  notes?: string | null;
}

export async function listExaminationTypes(): Promise<{
  items: ExaminationTypeRead[];
  total: number;
}> {
  const { data } = await api.get("/references/examination-types");
  return data;
}

export async function createExaminationType(
  payload: ExaminationTypeCreatePayload,
): Promise<ExaminationTypeRead> {
  const { data } = await api.post<ExaminationTypeRead>(
    "/references/examination-types",
    payload,
  );
  return data;
}

export async function deleteExaminationType(id: string): Promise<void> {
  await api.delete(`/references/examination-types/${id}`);
}

export interface MedicationRead {
  id: string;
  name: string;
  atc_code: string | null;
  dose_form: string | null;
  strengths: string[];
  notes: string | null;
  is_active: boolean;
}

export interface MedicationCreatePayload {
  name: string;
  atc_code?: string | null;
  dose_form?: string | null;
  strengths: string[];
  notes?: string | null;
}

export async function listMedications(params: {
  q?: string;
  limit?: number;
}): Promise<{ items: MedicationRead[]; total: number }> {
  const { data } = await api.get("/references/medications", { params });
  return data;
}

export async function createMedication(
  payload: MedicationCreatePayload,
): Promise<MedicationRead> {
  const { data } = await api.post<MedicationRead>("/references/medications", payload);
  return data;
}

export async function deleteMedication(id: string): Promise<void> {
  await api.delete(`/references/medications/${id}`);
}
